import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, throwError } from "rxjs";
import { environment } from '../../../environments/environment';
import { IBulkDistributionResponse, IBulkEditChanges, IBulkEditResponse, IBulkManagePair, IBulkManageResponse, IBulkTransitionResponse, ICreateLicenseNoteRequest, IDistributionInstruction, ILicenseLink, ILicenseNote, ILicenseUsage, ILicenseV2, ILicenseV2Request, IOrgOption, TransitionAction } from "../../common/models/license.interface";
import { mapLicenseV2Response, mapLicenseV2Request, mapDistributionInstructionRequest, mapBulkDistributionResponse, mapBulkTransitionResponse, mapLicenseUsageResponse, mapLicenseLinkResponse, mapBulkManagePairRequest, mapBulkManageLinksResponse, mapLicenseNoteResponse, mapCreateLicenseNoteRequest, mapBulkEditChangesRequest, mapBulkEditResponse } from "../../common/models/helpers/api-mappers";

@Injectable({
    providedIn: 'root'
})
export class LicenseV2Service {
    private httpClient = inject(HttpClient);

    private static readonly endpoints = {
        license: `${environment.apiUrlV2}/license`,
        licenses: `${environment.apiUrlV2}/licenses`,
        distribute: `${environment.apiUrlV2}/licenses/distribute`,
        transition: (action: TransitionAction) => `${environment.apiUrlV2}/licenses/${action}`,
        usage: (licenseId: string) => `${environment.apiUrlV2}/licenses/${licenseId}/usage`,
        licenseLinks: (licenseId: string) => `${environment.apiUrlV2}/licenses/${licenseId}/links`,
        batchLicenseLinks: `${environment.apiUrlV2}/licenses/links/batch`,
        linksByEntities: `${environment.apiUrlV2}/licenses/links/by-entities`,
        bulkManageLinks: `${environment.apiUrlV2}/licenses/bulk-manage-links`,
        bulkEdit: `${environment.apiUrlV2}/licenses/bulk-edit`,
        organisationChildren: `${environment.apiUrlV2}/organisations/children`,
        notes: `${environment.apiUrlV2}/license/notes`,
        noteById: (noteId: string) => `${environment.apiUrlV2}/license/notes/${noteId}`,
        notesByLicense: (licenseId: string) => `${environment.apiUrlV2}/licenses/${licenseId}/notes`,
        /** Reserved for future note linking/unlinking */
        linkNote: (noteId: string) => `${environment.apiUrlV2}/license/notes/${noteId}/link`,
        unlinkNote: (noteId: string) => `${environment.apiUrlV2}/license/notes/${noteId}/unlink`,
    };

    getLicenses(includeChildren: boolean = false): Observable<ILicenseV2[]> {
        const ERROR_MESSAGE = "Failed to get licenses";
        const params: Record<string, string> = {};
        if (includeChildren) {
            params['include_children'] = 'true';
        }
        return this.httpClient.get<any[]>(LicenseV2Service.endpoints.licenses, { params }).pipe(
            map((response: any[]) => response.map(mapLicenseV2Response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    getLicenseById(licenseId: string): Observable<ILicenseV2> {
        const ERROR_MESSAGE = `Failed to get license with id ${licenseId}`;
        return this.httpClient.get<any>(`${LicenseV2Service.endpoints.licenses}/${licenseId}`).pipe(
            map((response: any) => mapLicenseV2Response(response)), 
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    addLicense(license: ILicenseV2Request): Observable<ILicenseV2[]> {
        const ERROR_MESSAGE = "Failed to add license";
        return this.httpClient.post<any[]>(LicenseV2Service.endpoints.license, mapLicenseV2Request(license)).pipe(
            map((response: any[]) => response.map(mapLicenseV2Response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    updateLicense(license: ILicenseV2Request): Observable<ILicenseV2> {
        const ERROR_MESSAGE = "Failed to update license";
        return this.httpClient.put<any>(LicenseV2Service.endpoints.license, mapLicenseV2Request(license)).pipe(
            map((response: any) => mapLicenseV2Response(response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    deleteLicenses(ids: string[]): Observable<ILicenseV2[]> {
        const ERROR_MESSAGE = "Failed to delete licenses";
        return this.httpClient.delete<any[]>(LicenseV2Service.endpoints.licenses, { body: { ids } }).pipe(
            map((response: any[]) => response.map(mapLicenseV2Response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    bulkTransition(action: TransitionAction, ids: string[], prices?: Record<string, number>): Observable<IBulkTransitionResponse> {
        const ERROR_MESSAGE = `Failed to ${action} licenses`;
        const body: Record<string, unknown> = { ids };
        if (prices) {
            body['prices'] = prices;
        }
        return this.httpClient.post<any>(LicenseV2Service.endpoints.transition(action), body).pipe(
            map((response: any) => mapBulkTransitionResponse(response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    bulkEditLicenses(ids: string[], changes: IBulkEditChanges): Observable<IBulkEditResponse> {
        const ERROR_MESSAGE = "Failed to bulk edit licenses";
        const body = { ids, changes: mapBulkEditChangesRequest(changes) };
        return this.httpClient.post<any>(LicenseV2Service.endpoints.bulkEdit, body).pipe(
            map((response: any) => mapBulkEditResponse(response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    getLicenseUsage(licenseId: string): Observable<ILicenseUsage[]> {
        const ERROR_MESSAGE = `Failed to get usage for license ${licenseId}`;
        return this.httpClient.get<any[]>(LicenseV2Service.endpoints.usage(licenseId)).pipe(
            map((response: any[]) => response.map(mapLicenseUsageResponse)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    distributeLicenses(instructions: IDistributionInstruction[]): Observable<IBulkDistributionResponse> {
        const ERROR_MESSAGE = "Failed to distribute licenses";
        const body = { instructions: instructions.map(mapDistributionInstructionRequest) };
        return this.httpClient.post<any>(LicenseV2Service.endpoints.distribute, body).pipe(
            map((response: any) => mapBulkDistributionResponse(response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    getLicenseLinks(licenseId: string): Observable<ILicenseLink[]> {
        const ERROR_MESSAGE = `Failed to get links for license ${licenseId}`;
        return this.httpClient.get<any[]>(LicenseV2Service.endpoints.licenseLinks(licenseId)).pipe(
            map((response: any[]) => response.map(mapLicenseLinkResponse)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    getBatchLicenseLinks(licenseIds: string[]): Observable<{ licenseId: string; links: ILicenseLink[] }[]> {
        const ERROR_MESSAGE = 'Failed to get batch license links';
        return this.httpClient.post<any>(LicenseV2Service.endpoints.batchLicenseLinks, { ids: licenseIds }).pipe(
            map((response: any) => (response.results ?? []).map((entry: any) => ({
                licenseId: entry.license_id,
                links: (entry.links ?? []).map(mapLicenseLinkResponse),
            }))),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    getLinksByEntities(entityIds: string[]): Observable<ILicenseLink[]> {
        const ERROR_MESSAGE = "Failed to get links by entities";
        return this.httpClient.post<any[]>(LicenseV2Service.endpoints.linksByEntities, { entity_ids: entityIds }).pipe(
            map((response: any[]) => response.map(mapLicenseLinkResponse)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    bulkManageLinks(pairs: IBulkManagePair[]): Observable<IBulkManageResponse> {
        const ERROR_MESSAGE = "Failed to manage license links";
        const body = { pairs: pairs.map(mapBulkManagePairRequest) };
        return this.httpClient.post<any>(LicenseV2Service.endpoints.bulkManageLinks, body).pipe(
            map((response: any) => mapBulkManageLinksResponse(response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    // -----------------------------------------------------------------------
    // License Notes
    // -----------------------------------------------------------------------

    getNotesByLicense(licenseId: string): Observable<ILicenseNote[]> {
        const ERROR_MESSAGE = `Failed to get notes for license ${licenseId}`;
        return this.httpClient.get<any[]>(LicenseV2Service.endpoints.notesByLicense(licenseId)).pipe(
            map((response: any[]) => response.map(mapLicenseNoteResponse)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    createNote(request: ICreateLicenseNoteRequest): Observable<ILicenseNote> {
        const ERROR_MESSAGE = 'Failed to create license note';
        return this.httpClient.post<any>(LicenseV2Service.endpoints.notes, mapCreateLicenseNoteRequest(request)).pipe(
            map((response: any) => mapLicenseNoteResponse(response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    updateNote(noteId: string, note: string): Observable<ILicenseNote> {
        const ERROR_MESSAGE = `Failed to update note ${noteId}`;
        return this.httpClient.put<any>(LicenseV2Service.endpoints.noteById(noteId), { note }).pipe(
            map((response: any) => mapLicenseNoteResponse(response)),
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    deleteNotes(ids: string[]): Observable<void> {
        const ERROR_MESSAGE = 'Failed to delete license notes';
        return this.httpClient.delete<void>(LicenseV2Service.endpoints.notes, { body: { ids } }).pipe(
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }

    getChildOrganisations(includeDescendants: boolean = false): Observable<IOrgOption[]> {
        const ERROR_MESSAGE = 'Failed to get child organisations';
        const params: Record<string, string> = {};
        if (includeDescendants) {
            params['include_descendants'] = 'true';
        }
        return this.httpClient.get<IOrgOption[]>(LicenseV2Service.endpoints.organisationChildren, { params }).pipe(
            catchError(error => {
                console.error(ERROR_MESSAGE, error);
                return throwError(() => new Error(ERROR_MESSAGE));
            })
        );
    }
}
